import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useEditorStore } from '../../stores/editorStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useDebounce } from '../../hooks/usePerformance';
import { useScreenReaderAnnouncement } from '../../hooks/useAccessibility';
import LexicalEditor from './LexicalEditor';

const FocusEditor: React.FC = () => {
  const { activeDocument, updateDocument } = useEditorStore();
  const { 
    fontFamily, 
    fontSize, 
    lineHeight, 
    maxWidth,
    editorOpacity,
    editorBlur,
    useGlassmorphism,
    editorHeight,
    textColor
  } = useSettingsStore();
  const { manualSave } = useAutoSave();
  const [content, setContent] = useState(activeDocument?.content || '');
  const announce = useScreenReaderAnnouncement();
  
  // 성능 최적화: 디바운싱된 업데이트
  const debouncedContent = useDebounce(content, 300);

  useEffect(() => {
    setContent(activeDocument?.content || '');
    if (activeDocument) {
      announce(`Switched to document: ${activeDocument.title}`);
    }
  }, [activeDocument, announce]);


  // 디바운싱된 문서 업데이트
  useEffect(() => {
    if (activeDocument && debouncedContent !== activeDocument.content) {
      updateDocument(activeDocument.id, debouncedContent);
    }
  }, [debouncedContent, activeDocument, updateDocument]);

  const handleChange = useCallback((newContent: string) => {
    setContent(newContent);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // 💾 Ctrl+S 또는 Cmd+S: 수동 저장
    if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault();
      manualSave();
      announce('Document saved');
    }
  };

  const getFontClass = () => {
    switch (fontFamily) {
      case 'serif':
        return 'font-serif';
      case 'sans':
        return 'font-sans';
      case 'mono':
        return 'font-mono';
      default:
        return 'font-serif';
    }
  };

  const getTextColorClass = () => {
    switch (textColor) {
      case 'white':
        return 'text-white';
      case 'black':
        return 'text-black';
      default:
        return 'text-white';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="w-full h-full flex flex-col items-center justify-center px-4 py-8"
    >
      <div
        className={`w-full rounded-2xl shadow-2xl ${useGlassmorphism ? 'glassmorphism-editor' : 'bg-white/90 dark:bg-gray-900/90'}`}
        style={{ 
          maxWidth: `${maxWidth}px`,
          ...(useGlassmorphism && {
            background: `rgba(255, 255, 255, ${editorOpacity})`,
            backdropFilter: `blur(${editorBlur}px) saturate(180%)`,
            WebkitBackdropFilter: `blur(${editorBlur}px) saturate(180%)`,
          })
        }}
      >
        <LexicalEditor
          initialContent={content}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Start writing your story..."
          className={`
            w-full p-8
            bg-transparent
            ${getFontClass()}
            ${getTextColorClass()}
            placeholder-gray-500 dark:placeholder-gray-400
            resize-none
            focus:outline-none focus:ring-0
            selection:bg-blue-200/50 dark:selection:bg-blue-800/50
            transition-all duration-300
          `}
          style={{
            fontSize: `${fontSize}px`,
            lineHeight: lineHeight,
            minHeight: `${editorHeight}vh`,
            maxHeight: `${editorHeight}vh`,
            overflowY: 'auto',
          }}
        />
        <div 
          id="editor-help" 
          className="sr-only"
          aria-live="polite"
        >
          Use Ctrl+S (or Cmd+S) to save, Ctrl+N for new document, ESC for fullscreen, Ctrl+B for background themes
        </div>
      </div>
    </motion.div>
  );
};

export default FocusEditor;